/* eslint-disable max-nested-callbacks */
import { defineStore } from 'pinia'

import { ENOVIA_GROUP_STATUS_NAME } from '~/config/tagger-or-enovia'

import type { IMedia } from '~/entities/content/media/interfaces/media'
import type { IUpdateWedoParams } from '~/entities/content/wedo/api/crud-wedos'
import { ASSIGNEE_OPERATION, WEDO_CRUD_API } from '~/entities/content/wedo/api/crud-wedos'
import { toMaturity } from '~/entities/content/wedo/api/exalead-wedos'
import type { IWedo } from '~/entities/content/wedo/interfaces/wedo'

import SwymServices from '~/features-shared/content/api/contents'
import ExaleadServices from '~/features-shared/content/api/exalead-contents'
import { IContentModel } from '~/features-shared/content/interfaces/content-model'
import { contentsMap, mergeContent } from '~/features-shared/content/stores/contents-db'
import type { TagActions } from '~/features-shared/content-derived-type/utils/derived-types'
import { useRefineGlobalContentsStore } from '~/features-shared/content-feed/composables/refine-contents'
import { ServiceFetchControllerDefault } from '~/features-shared/content-feed/controllers/services-fetch-controller-default'
import type { WedoDS6WMaturity } from '~/features-shared/maturities/interfaces/exalead-maturities'
import { WedoMaturity } from '~/features-shared/maturities/interfaces/maturities'

import { SERVICE } from '@ds/swymkit/tools'
import type { WithRequired } from '~/features-generic/libs/interfaces/utils'

const WEDO_EXALEAD_STATUS_GROUP_NAME = ENOVIA_GROUP_STATUS_NAME

export const useRefineWedosStore = defineStore('refineWedos', () => {
  const store = useRefineGlobalContentsStore(
    new ServiceFetchControllerDefault(
      SwymServices[IContentModel.Wedo],
      ExaleadServices[IContentModel.Wedo],
    ),
  )

  const maturities = computed<Record<WedoMaturity, number>>(() => {
    const groups = store.getGroups(store.context.value) || {}
    const status = groups && groups.find(g => g.id === WEDO_EXALEAD_STATUS_GROUP_NAME)
    const category = status && status.categories && status.categories.length && status.categories[0]
    const subCategories = category && category.categories

    const defaultMaturities = {
      [WedoMaturity.Open]: 0,
      [WedoMaturity.InProgress]: 0,
      [WedoMaturity.Done]: 0,
    }
    const extractedMaturities = subCategories && subCategories
      .map(c => ({ id: toMaturity(c.title as WedoDS6WMaturity), count: c.count || 0 }))
      .reduce((pre, cur) => ({ ...pre, ...{ [cur.id]: cur.count } }), {})
    const maturities = extractedMaturities && {
      ...defaultMaturities,
      ...extractedMaturities,
    }
    return maturities || { [WedoMaturity.Open]: 0, [WedoMaturity.InProgress]: 0, [WedoMaturity.Done]: 0 }
  })

  const computations = {
    open: computed(() => maturities.value[WedoMaturity.Open]),
    inprogress: computed(() => maturities.value[WedoMaturity.InProgress]),
    done: computed(() => maturities.value[WedoMaturity.Done]),
  }

  const actions = {
    async doUpdateMaturity(subject_uri: string, maturity: WedoMaturity) {
      const content = (contentsMap.get(subject_uri) || {}) as IWedo
      const previous = content.maturity
      content.maturity = maturity

      try {
        await WEDO_CRUD_API.update({ subject_uri, maturity })
      }
      catch (error) {
        content.maturity = previous
      }

      return content
    },
    async doUpdateEndDate(subject_uri: string, end_date: string) {
      const content = (contentsMap.get(subject_uri) || {}) as WithRequired<IWedo, 'attributes'>
      const previous = content.attributes['dsxplan:estimatedEndDate']
      content.attributes['dsxplan:estimatedEndDate'] = end_date

      try {
        await WEDO_CRUD_API.update({ subject_uri, end_date })
      }
      catch (error) {
        content.attributes['dsxplan:estimatedEndDate'] = previous
      }

      return content
    },
    async doAddAssignee(subject_uri: string, uid: string) {
      const content = (contentsMap.get(subject_uri) || {}) as WithRequired<IWedo, 'attributes'>
      const response = await WEDO_CRUD_API.update({
        subject_uri,
        assignees_ops: [{ operation: ASSIGNEE_OPERATION.ADD, value: uid }],
      })
      Object.assign(content.attributes, response.result.attributes)

      return response.result
    },
    async doRemoveAssignee(subject_uri: string, uid: string) {
      const content = (contentsMap.get(subject_uri) || {}) as WithRequired<IWedo, 'attributes'>
      const response = await WEDO_CRUD_API.update({
        subject_uri,
        assignees_ops: [{ operation: ASSIGNEE_OPERATION.REMOVE, value: uid }],
      })
      Object.assign(content.attributes, response.result.attributes)

      return response.result
    },
    async doUpdate(subject_uri: string, params: {
      title: string
      description: string
      end_date: string
      maturity: WedoMaturity
      assignees_to_add: string[]
      assignees_to_remove: string[]
      attachments_to_add: IMedia[]
      attachments_to_remove: IMedia[]
      tags6w?: any[]
      taggingActions?: TagActions
    }) {
      const _params: IUpdateWedoParams = {
        subject_uri,
        title: params.title,
        message: params.description,
        end_date: params.end_date,
        maturity: params.maturity,
        assignees_ops: [
          ...params.assignees_to_add.map(el => ({ operation: ASSIGNEE_OPERATION.ADD, value: el })),
          ...params.assignees_to_remove.map(el => ({ operation: ASSIGNEE_OPERATION.REMOVE, value: el })),
        ],
        attachments_to_add: params.attachments_to_add
          .map(el => ({ id: el.subject_uri, title: el.title, serviceHosting: SERVICE.SWYM })),
        attachments_to_remove: params.attachments_to_remove
          .map(el => ({ id: el.subject_uri, title: el.title, serviceHosting: SERVICE.SWYM })),
        taggingActions: params.taggingActions,
      }
      const response = await WEDO_CRUD_API.update(_params)
      mergeContent(response.result)

      return response.result
    },
  }

  return {
    // States
    maturities,
    groups: store.groups,

    // Computed
    total: store.total,
    currentContents: store.currentContents,
    limit: store.limit,

    open: computations.open,
    inprogress: computations.inprogress,
    done: computations.done,

    // Tags
    tags: store.tags,
    tagsWithSubjectURIData: store.tagsWithSubjectURIData,
    tagsWithoutSubjectURIData: store.tagsWithoutSubjectURIData,

    // Getters
    getContents: store.getContents,

    // Actions
    doFetchNext: store.doFetchNext,
    doFetchMeta: store.doFetchMeta,
    doInsert: store.doInsert,
    doClear: store.doClear,
    doClearGroups: store.doClearGroups,
    doUpdateMaturity: actions.doUpdateMaturity,
    doUpdateEndDate: actions.doUpdateEndDate,
    doUpdate: actions.doUpdate,
    doAddAssignee: actions.doAddAssignee,
    doRemoveAssignee: actions.doRemoveAssignee,
  }
})
